import { SxProps, Theme } from "@mui/material";

export const darkInput: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.05)",
    borderRadius: "12px",
    color: "white",
    fontFamily: "'Barlow', sans-serif",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(81,242,203,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#51F2CB" },
    "&.Mui-disabled": {
      color: "white",
      WebkitTextFillColor: "white",
      "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: "white",
      WebkitTextFillColor: "white",
    },
    "& .MuiSelect-icon.Mui-disabled": {
      color: "rgba(255,255,255,0.5)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.5)",
    fontFamily: "'Barlow', sans-serif",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#51F2CB" },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "rgba(255,255,255,0.5)",
  },
};

export const selectMenuProps = {
  PaperProps: {
    sx: {
      background: "#0b1120",
      border: "1px solid rgba(255,255,255,0.12)",
      "& .MuiMenuItem-root": {
        fontFamily: "'Barlow', sans-serif",
        color: "rgba(255,255,255,0.82)",
        "&:hover": { background: "rgba(81,242,203,0.1)" },
        "&.Mui-selected": {
          background: "rgba(81,242,203,0.15)",
          color: "#51F2CB",
          "&:hover": { background: "rgba(81,242,203,0.2)" },
        },
      },
    },
  },
};

export const ctaButton: SxProps<Theme> = {
  background: "linear-gradient(90deg, #f97316, #fb923c)",
  color: "white",
  borderRadius: "50px",
  py: 1.75,
  fontSize: "1rem",
  fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
  textTransform: "none" as const,
  boxShadow: "0 4px 24px rgba(249,115,22,0.35)",
  "&:hover": { background: "linear-gradient(90deg, #ea6c0a, #f97316)", color: "white" },
};

export const backButton: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.5)",
  textTransform: "none" as const,
  fontSize: "0.9rem",
  "&:hover": { color: "#51F2CB", background: "transparent" },
};

export const gradientText: SxProps<Theme> = {
  background: "linear-gradient(90deg, #51F2CB, #20C2F1)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export const eyebrow: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.2em",
  textTransform: "uppercase" as const,
  color: "#51F2CB",
  mb: 1.5,
};

export const stepHeading: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  mb: 4,
  color: "white",
};

export const stepIndicatorContainer: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 6,
  gap: 0,
};

export const stepCircle = (isComplete: boolean, isActive: boolean): SxProps<Theme> => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: isComplete || isActive ? "#51F2CB" : "rgba(255,255,255,0.08)",
  border: isComplete || isActive ? "none" : "1px solid rgba(255,255,255,0.15)",
  transition: "all 0.3s ease",
});

export const stepNumber = (isActive: boolean): SxProps<Theme> => ({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: "0.85rem",
  color: isActive ? "#040922" : "rgba(255,255,255,0.3)",
});

export const stepLabel = (isComplete: boolean, isActive: boolean): SxProps<Theme> => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: "0.65rem",
  fontWeight: 500,
  letterSpacing: "0.05em",
  color: isComplete || isActive ? "#51F2CB" : "rgba(255,255,255,0.3)",
  transition: "color 0.3s ease",
});

export const stepConnector = (isComplete: boolean): SxProps<Theme> => ({
  height: "1px",
  width: 48,
  background: isComplete ? "#51F2CB" : "rgba(255,255,255,0.12)",
  mb: 2.5,
  mx: 0.5,
  transition: "background 0.3s ease",
});

export const introHeading: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: { xs: "2rem", md: "2.6rem" },
  mb: 2,
  color: "white",
};

export const introPrice: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: { xs: "2.4rem", md: "3rem" },
  lineHeight: 1,
  mb: 4,
  background: "linear-gradient(90deg, #51F2CB, #20C2F1)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export const introDisclaimer: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.82rem",
  color: "rgba(255,255,255,0.4)",
  mt: 2,
};

export const businessModelInput: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.05)",
    borderRadius: "12px",
    color: "white",
    fontFamily: "'Barlow', sans-serif",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(81,242,203,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#51F2CB" },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.5)",
    fontFamily: "'Barlow', sans-serif",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#51F2CB" },
  "& .MuiFormHelperText-root": {
    color: "rgba(255,255,255,0.4)",
    fontFamily: "'Barlow', sans-serif",
  },
};

export const primaryOwnersTitle: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: "1rem",
  color: "white",
  mb: 0.5,
};

export const primaryOwnersSubtitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.85rem",
  color: "rgba(255,255,255,0.45)",
  mb: 2.5,
};

export const ownerCard: SxProps<Theme> = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(81,242,203,0.1)",
  borderRadius: "16px",
  p: 2.5,
  mb: 2,
  position: "relative",
  overflow: "hidden",
};

export const removeOwnerButton: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.4)",
  textTransform: "none",
  fontSize: "0.8rem",
  minWidth: "auto",
  p: 0,
  "&:hover": { color: "#f87171", background: "transparent" },
};

export const addOwnerButton: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "#51F2CB",
  textTransform: "none",
  fontSize: "0.9rem",
  p: 0,
  mb: 3,
  "&:hover": { background: "transparent", opacity: 0.8 },
};

export const successHeading: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: { xs: "1.6rem", md: "2rem" },
  mb: 1.5,
  background: "linear-gradient(90deg, #51F2CB, #20C2F1)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export const successSubtitle: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: "1rem",
  color: "white",
  mb: 1.5,
};

export const successBody: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: "1rem",
  mb: 4,
};

export const pageWrapper: SxProps<Theme> = {
  minHeight: "100vh",
  background: "#040922",
  position: "relative",
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export const backgroundOverlay: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background:
    "radial-gradient(ellipse at 20% 10%, rgba(81,242,203,0.06) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(32,194,241,0.05) 0%, transparent 60%)",
  pointerEvents: "none",
};

export const containerWrapper = (isIntro: boolean): SxProps<Theme> => ({
  position: "relative",
  zIndex: 1,
  py: { xs: 3, md: 4 },
  ...(isIntro && {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  }),
});

export const formCard: SxProps<Theme> = {
  background: "#0b1120",
  border: "1px solid rgba(81,242,203,0.12)",
  borderRadius: "20px",
  p: { xs: 3, md: 5 },
  position: "relative",
  overflow: "hidden",
};

export const backToEnterpriseButton: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.35)",
  textTransform: "none",
  fontSize: "0.85rem",
  px: 0,
  "&:hover": { color: "#51F2CB", background: "transparent" },
};
